import React, { useContext, useState } from 'react';
import { NotebookContext } from '../context/NotebookContext';
import client from '../lib/client';
import { createOrUpdateSubject } from '../queries/subjects';
import {
  CreateOrUpdateSubject,
  CreateOrUpdateSubjectVariables
} from '../queries/__generated__/CreateOrUpdateSubject';
import ErrorMessages from './ErrorMessages';
import { Input } from './forms';
import { Modal } from './ui';
import { PureQueryOptions } from 'apollo-client';
import { selectedNotebookQuery } from '../queries/notebooks';
import { SelectedNotebookVariables } from '../queries/__generated__/SelectedNotebook';
import { groupBySlugQuery } from '../queries/groups';
import { GroupBySlugQueryVariables } from '../queries/__generated__/GroupBySlugQuery';
import { toast } from 'react-toastify';
import redirect from '../lib/redirect';
import { UserContext } from '../context/UserContext';

interface NewSubjectModalProps {
  closeModal: () => void;
  redirectToSubject?: boolean;
  group?: {
    id: string;
    slug: string;
  };
}

const NewSubjectModal: React.FC<NewSubjectModalProps> = ({
  closeModal,
  group,
  redirectToSubject
}) => {
  const [subjectName, setSubjectName] = useState('');
  const [errors, setErrors] = useState<{ message: string }[]>([]);
  const { notebook, reloadNotebook } = useContext(NotebookContext);
  const { reloadUser } = useContext(UserContext);

  async function createSubject() {
    let variables: CreateOrUpdateSubjectVariables = {
      input: {
        name: subjectName,
        notebookId: notebook.id
      }
    };

    let refetchQueries: PureQueryOptions[] = [
      { query: selectedNotebookQuery, variables: { id: notebook.id } as SelectedNotebookVariables }
    ];

    if (group) {
      variables.input.groupIds = [group.id];
      refetchQueries.push({
        query: groupBySlugQuery,
        variables: { slug: group.slug, notebookId: notebook.id } as GroupBySlugQueryVariables
      });
    }

    const res = await client.mutate<CreateOrUpdateSubject, CreateOrUpdateSubjectVariables>({
      mutation: createOrUpdateSubject,
      variables,
      refetchQueries
    });

    const { subject, errors } = res.data!.createOrUpdateSubject;

    if (errors) {
      setErrors(errors);
      return;
    }

    toast(`New subject created: ${subject!.name}`, { type: 'success' });
    reloadNotebook();
    reloadUser();

    if (subject && redirectToSubject) {
      redirect(`/notebook/subject/${subject.slug}`);
    } else {
      closeModal();
    }
  }

  return (
    <Modal closeModal={closeModal}>
      <div className="pa4 navy">
        <h1 className="f4 fw6 mb3 tc">New Subject</h1>

        <div className="mb3 tc">
          <ErrorMessages errors={errors} />
        </div>

        <form
          onSubmit={e => {
            e.preventDefault();
            if (subjectName) {
              createSubject();
            }
          }}
        >
          <Input
            value={subjectName}
            onChange={e => setSubjectName(e.target.value)}
            placeholder="Subject name..."
            data-testid="subject-name-input"
            autoFocus
          />
        </form>
      </div>

      <div className="flex bt b--light-gray navy">
        <button
          className="w-50 br b--light-gray f6 pa3 tc blue hover-navy"
          onClick={closeModal}
          data-testid="cancel-button"
        >
          Cancel
        </button>

        <button
          className="w-50 f6 pa3 tc blue hover-navy"
          onClick={createSubject}
          disabled={!subjectName}
          data-testid="create-subject-button"
        >
          Create Subject
        </button>
      </div>
    </Modal>
  );
};

export default NewSubjectModal;
